import { deepGet, scrollToDistance } from '@kk/utils';
import { formatModuleId, getElementTop, globalInstance } from '@kk-activity-factory/utils';
import Bus, { busEvent } from '@kk-activity-factory/bus';
import { defaultActive } from '@kk-activity-factory/filter-module/defaultActive';

class PageTree {
    constructor(root) {
        /**
         * 是否是根节点
         */
        this.root = root;
        /**
         * 当前展示的模块
         */
        this.modules = [];
        /**
         * 包含的子节点
         */
        this.children = [];
        /**
         * 父节点
         */
        this.parent = null;
    }

    push(id) {
        this.modules.push(id);
    }

    relationChild(child) {
        this.children.push(child);
        child.relationParent(this);
    }

    relationParent(parent) {
        this.parent = parent;
    }
}

class PageFilter extends PageTree {
    constructor(modules) {
        super(false);
        /**
         * 筛选分页原始数据
         */
        this.originData = modules;
        /**
         * 被筛选的模块数据（数组顺序和筛选分页的tab顺序一致）
         */
        this.contains = this.getOriginFilter().map((item) =>
            item.type === 1 ? item.relateMouldIds.map((id) => this.safeModuleId(id)) : []
        );
        /**
         * 筛选分页id
         */
        this.id = this.getId();
    }

    safeModuleId(id) {
        return formatModuleId(id);
    }

    getOriginFilter() {
        return deepGet(this.originData, 'props.rowData.styleDetail.filters', []);
    }

    getId() {
        return deepGet(this.originData, 'props.rowData.tradingSourceIdentifier', []);
    }

    /**
     * 检查一个模块id是否被本筛选器筛选中
     * @param {*} moduleId 模块的id
     * @returns
     */
    hasModuleBeenFiltered(moduleId) {
        if (this.root) {
            return false;
        }
        const id = this.safeModuleId(moduleId);
        let result = false;
        this.contains.forEach((filter) => {
            if (filter.includes(id)) {
                result = true;
            }
        });
        return result;
    }

    isFirstlevelFilter() {
        return deepGet(this, 'parent.root', false);
    }

    isSecondlevelFilter() {
        return deepGet(this, 'parent.parent.root', false);
    }
}

class PageDisplayManager {
    constructor() {
        this.modulesMap = new Map();
        this.pageFilters = new Map();
        this.firstLevelFilter = new Map();
        this.secondLevelFilter = new Map();
        this.pageTree = null;
        this.currentList = [];
    }

    init(displayModuleStyleConfigList) {
        const allModuleIds = [];

        displayModuleStyleConfigList.forEach((module) => {
            const rowData = deepGet(module, 'props.rowData', null);
            if (!rowData) {
                return; // 存在不支持的模块，直接跳出
            }
            this.modulesMap.set(module.props.rowData.tradingSourceIdentifier, module);
            allModuleIds.push(module.props.rowData.tradingSourceIdentifier);
            if (this.isFilterModule(module)) {
                const pageFilter = new PageFilter(module);
                this.pageFilters.set(pageFilter.getId(), pageFilter);
            }
        });
        const pageTree = new PageTree(true);
        allModuleIds.forEach((id) => {
            let filterCount = 0;
            Array.from(this.pageFilters.values()).forEach((pageFilter) => {
                if (pageFilter.hasModuleBeenFiltered(id)) {
                    const filter = this.pageFilters.get(id);
                    if (filter) {
                        pageFilter.relationChild(filter);
                    }
                    filterCount++;
                }
            });
            if (filterCount === 0) {
                // 没有被筛选的模块
                pageTree.push(id);
                const filter = this.pageFilters.get(id);
                if (filter) {
                    pageTree.relationChild(filter);
                }
            } else if (filterCount > 1) {
                // 异常
                console.error(`模块${id}被两个或以上的筛选分页模块筛选`);
            }
        });
        this.pageTree = pageTree;
        Array.from(this.pageFilters.values()).forEach((filter) => {
            if (filter.isFirstlevelFilter()) {
                this.firstLevelFilter.set(filter.getId(), []);
            }
            if (filter.isSecondlevelFilter()) {
                this.secondLevelFilter.set(filter.getId(), []);
            }
        });
        this.initFilter();
        return this.buildList();
    }

    /**
     * 初始化激活全部的筛选分页模块
     */
    initFilter() {
        const store = globalInstance.getRootStore();
        let now = new Date().getTime();
        if (store.state.pageData) {
            // 如果已经获取到服务器时间
            now = store.state.pageData.now;
        }
        // 当存在一级筛选分页模块的时候
        if (this.firstLevelFilter.size > 0) {
            this.firstLevelFilter.forEach((_value, key) => {
                this.initActiveFiler(key, now);
            });
        }
        // 当存在二级筛选分页模块
        if (this.secondLevelFilter.size > 0) {
            let firstLevel = [];
            for (const value of this.firstLevelFilter.values()) {
                firstLevel = firstLevel.concat(value);
            }
            // firstLevel中是所有被一级筛选分页模块激活的模块
            // 需要从中找出二级筛选分页模块进行初始化激活
            for (const key of firstLevel) {
                const filter = this.pageFilters.get(key);
                if (filter && filter.isSecondlevelFilter()) {
                    this.initActiveFiler(key, now);
                }
            }
        }
    }

    /**
     * 初始化激活一个筛选分页模块
     * @param { string } key 模块id
     * @param { number } now 当前服务器时间
     */
    initActiveFiler(key, now) {
        const {
            props: { rowData },
        } = this.modulesMap.get(key);
        // 获取筛选分页模块应该激活的分页信息
        const result = defaultActive(rowData, now);
        if (result) {
            // 可以激活的时候，进行一次激活
            // 这样，在init函数返回结果前，所有筛选分页都会被成功激活，避免不必要的二次激活
            this.activeFilter(result);
        }
    }

    activeFilter({ id, activeIndex }) {
        if (this.pageFilters.get(id).isFirstlevelFilter()) {
            return this.activieFirstLevelFilter(id, activeIndex);
        }

        if (this.pageFilters.get(id).isSecondlevelFilter()) {
            return this.activieSecondLevelFilter(id, activeIndex);
        }
    }

    activieFirstLevelFilter(id, activeIndex) {
        const filter = this.pageFilters.get(id);
        const contains = filter.contains[activeIndex];
        this.firstLevelFilter.set(filter.getId(), [...contains]);
        return this.buildList();
    }

    activieSecondLevelFilter(id, activeIndex) {
        const filter = this.pageFilters.get(id);
        const contains = filter.contains[activeIndex];
        this.secondLevelFilter.set(filter.getId(), [...contains]);
        return this.buildList();
    }

    buildList() {
        const baseList = [...this.pageTree.modules];
        let firstLevel = [];
        for (const value of this.firstLevelFilter.values()) {
            firstLevel = firstLevel.concat(value);
        }
        let secondLevel = [];
        for (const key of firstLevel) {
            const list = this.secondLevelFilter.get(key);
            if (Array.isArray(list)) {
                secondLevel = secondLevel.concat(list);
            }
        }
        this.currentList = baseList.concat(firstLevel, secondLevel);
        const renderSet = new Set();
        this.currentList.forEach((id) => renderSet.add(id));
        return Array.from(renderSet)
            .map((id) => {
                const rowData = this.getModuleRawData(id);
                if (rowData) {
                    return rowData;
                } else {
                    console.warn(`没有找到${id}的数据`); // 一般是被筛选的模块已经过期了
                    return undefined;
                }
            })
            .filter((item) => {
                // eslint-disable-next-line sonarjs/prefer-single-boolean-return
                if (item) {
                    return true;
                } else {
                    return false;
                }
            });
    }

    toString() {
        return `${this.id}`;
    }

    getLocationType(modules) {
        return deepGet(modules, 'props.rowData.moduleLocationInfo.locationType', 'PERMUTATION');
    }

    getStaticOrder(modules) {
        return deepGet(modules, 'props.rowData.moduleLocationInfo.moduleLocation');
    }

    getDisplayModuleType(modules) {
        return deepGet(modules, 'props.rowData.displayModuleType', 'PERMUTATION');
    }

    getModuleRawData(id) {
        return this.modulesMap.get(id);
    }

    isFilterModule(modules) {
        return this.getDisplayModuleType(modules) === 'TABBED_PAGE';
    }

    isInCurrentList(id) {
        return this.currentList.includes(id);
    }

    seekTabActivePath(id) {
        const safeId = formatModuleId(id);
        if (this.isInCurrentList(safeId)) {
            // 不需要激活
            console.info(`${safeId}在当前页面中已经激活，不需要激活`);
            return [];
        }
        const moduleData = this.modulesMap.get(safeId);
        if (!moduleData) {
            console.info(`${safeId}不存在，无法寻找路径`);
            return [];
        }
        if (this.isFilterModule(moduleData)) {
            // 说明是一个被筛选的二级tab，查询一级路径
            return [this.seekFirstTabActivePath(safeId)];
        } else {
            for (const filter of this.pageFilters.values()) {
                const filterTabLength = filter.contains.length;
                for (let i = 0; i < filterTabLength; i++) {
                    const contain = filter.contains[i];
                    if (contain.includes(safeId)) {
                        console.info(i, filter.isFirstlevelFilter(), filter.isSecondlevelFilter());
                        if (filter.isFirstlevelFilter()) {
                            // 被一级tab筛选
                            return [
                                {
                                    id: filter.getId(),
                                    activeIndex: i,
                                },
                            ];
                        }
                        if (filter.isSecondlevelFilter()) {
                            // 被二级tab筛选
                            return [
                                this.seekFirstTabActivePath(filter.getId()),
                                {
                                    id: filter.getId(),
                                    activeIndex: i,
                                },
                            ];
                        }
                    }
                }
            }
        }
        return [];
    }

    seekFirstTabActivePath(safeId) {
        for (const filter of this.pageFilters.values()) {
            const filterTabLength = filter.contains.length;
            for (let i = 0; i < filterTabLength; i++) {
                const contain = filter.contains[i];
                if (contain.includes(safeId)) {
                    return {
                        id: filter.getId(),
                        activeIndex: i,
                    };
                }
            }
        }
    }

    activeModule(id) {
        const safeId = formatModuleId(id);
        if (this.isInCurrentList(safeId)) {
            this.scroolToModule(safeId);
        } else {
            const path = this.seekTabActivePath(safeId);
            if (path.length === 1) {
                loading.show();
                Bus.$emit(busEvent.SWITCH_PAGE_FILTER_MOUDLE_FROM_APP, path[0]);
                setTimeout(() => {
                    loading.hide();
                    this.scroolToModule(safeId);
                }, 500);
            } else if (path.length === 2) {
                loading.show();
                Bus.$emit(busEvent.SWITCH_PAGE_FILTER_MOUDLE_FROM_APP, path[0]);
                setTimeout(() => {
                    Bus.$emit(busEvent.SWITCH_PAGE_FILTER_MOUDLE_FROM_APP, path[1]);
                    setTimeout(() => {
                        loading.hide();
                        this.scroolToModule(safeId);
                    }, 500);
                }, 500);
            }
        }
    }

    scroolToModule(id) {
        try {
            const targetDom = document.querySelector(`[data-moduleid="${formatModuleId(id)}"]`);
            const top = getElementTop(targetDom);
            const distance = top - (document.documentElement.scrollTop || document.body.scrollTop);
            scrollToDistance(document.documentElement, distance, 500);
            scrollToDistance(document.body, distance, 500);
        } catch (error) {
            console.error('scroolToModule', error);
        }
    }
}

export const pageDisplayManager = new PageDisplayManager();
